import Send from '@mui/icons-material/Send';
import { Box, TextField } from '@mui/material';
import { addMessageToRequest, getMessagesFromRequest } from 'lib/api/chat';
import {
  ChatMessageBubble,
  ChatSkeleton,
  ErrorState,
  MobileBody,
  RoundIconButton,
} from 'lib/mobileDesignSystem';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

export interface ChatMessage {
  userId: number;
  userName: string;
  userRole: string;
  text: string;
  timestamp: Date | string;
  loading?: boolean;
}

export type FinanceChatRequestType = 'factoring' | 'confirming' | 'creditLine';

export interface SharedChatProps {
  requestId: string;
  requestType: FinanceChatRequestType;
}

const SharedChat: React.FC<SharedChatProps> = ({ requestId, requestType }) => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const currentUserId = user?.id ?? -1;

  const operationId =
    requestId != null && requestId !== '' ? Number(requestId) : null;
  const hasValidOperationId = operationId != null && !Number.isNaN(operationId);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageToSend, setMessageToSend] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const fetchMessages = useCallback(
    async (showLoading = true) => {
      if (!hasValidOperationId) return;
      if (showLoading) setLoading(true);
      try {
        const result = await getMessagesFromRequest(dispatch, {
          requestType,
          requestId: operationId,
        });
        const list = result?.messages ?? [];
        setMessages(list);
      } catch {
        setMessages([]);
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [dispatch, operationId, requestType],
  );

  useEffect(() => {
    if (hasValidOperationId) {
      void fetchMessages();
    } else {
      setLoading(false);
    }
  }, [hasValidOperationId, fetchMessages]);

  const sendMessage = async () => {
    const text = messageToSend.trim();
    if (!text || !hasValidOperationId || sending) return;

    const role = user?.roles?.[0]?.name ?? '';
    const optimistic: ChatMessage = {
      userId: currentUserId,
      userName: user?.name ?? '',
      userRole: role,
      text,
      timestamp: new Date(),
      loading: true,
    };
    setMessageToSend('');
    setMessages((prev) => [...prev, optimistic]);
    setSending(true);

    try {
      await addMessageToRequest(dispatch, {
        requestType,
        requestId: operationId,
        message: text,
      });
      await fetchMessages(false);
    } catch {
      setMessages((prev) =>
        prev.map((m) => (m === optimistic ? { ...m, loading: false } : m)),
      );
    } finally {
      setSending(false);
    }
  };

  if (requestId == null || requestId === '') {
    return <ErrorState message="Operación no válida." />;
  }

  if (!hasValidOperationId) {
    return <ErrorState message="Identificador de operación no válido." />;
  }

  if (loading && messages.length === 0) {
    return <ChatSkeleton />;
  }

  const CHAT_INPUT_BAR_HEIGHT = 15;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        px: 0,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 2,
          py: 2,
          pb: CHAT_INPUT_BAR_HEIGHT,
          display: 'flex',
          flexDirection: 'column-reverse',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[...messages].reverse().map((message, index, reversed) => {
            const previousDate =
              index > 0
                ? new Date(reversed[index - 1].timestamp).toDateString()
                : null;
            const currentDate = new Date(message.timestamp).toDateString();
            const showDateSeparator =
              index === 0 ||
              (previousDate != null && currentDate !== previousDate);

            return (
              <Box key={`${message.timestamp}-${index}`}>
                {showDateSeparator && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      my: 2,
                    }}
                  >
                    <MobileBody
                      component="span"
                      sx={{
                        backgroundColor: 'grey.200',
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                      }}
                    >
                      {new Date(message.timestamp).toLocaleDateString('es-ES', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </MobileBody>
                  </Box>
                )}
                <ChatMessageBubble
                  align={message.userId === currentUserId ? 'right' : 'left'}
                  primaryText={`${message.userName} | ${message.userRole}`}
                  messageText={message.text}
                  timestamp={message.timestamp}
                  loading={message.loading === true}
                />
              </Box>
            );
          })}
        </Box>
        {messages.length === 0 && !loading && (
          <MobileBody color="text.secondary" sx={{ py: 2 }}>
            No hay mensajes todavía.
          </MobileBody>
        )}
      </Box>

      <Box
        component="footer"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <TextField
          value={messageToSend}
          onChange={(e) => setMessageToSend(e.target.value)}
          variant="outlined"
          fullWidth
          size="small"
          placeholder="Escribe un mensaje..."
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiOutlinedInput-root': { borderRadius: 3 },
          }}
        />
        <RoundIconButton
          icon={<Send fontSize="small" />}
          onClick={() => void sendMessage()}
          color="primary"
          disabled={sending || !messageToSend.trim()}
          aria-label="Enviar mensaje"
        />
      </Box>
    </Box>
  );
};

export default SharedChat;
